import { projectWebMercator } from './geospatial_conversions';

function projectShapes(latLngs) {
  return latLngs.map(points => points.map(([lat, lng]) => projectWebMercator(lat, lng)));
}

function extractShapes(shapes) {
  return shapes.map(points => points.map(([y, x]) => [x, -y]));
}

function calculateBounds(shapes) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity
  let maxY = -Infinity;
  shapes.forEach(points => points.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }));
  return [minX, minY, maxX, maxY];
}

/**
 * Fits a given polygon within a 1x1 square whose origin is 0,0.
 *  Maintains aspect ratio of the shape and also centers it within the square.
 *  It also switches the coordinate space from lat,lng to x,y
 * @param {Array<Array<Array<number>>>} shapes - [[[latitude/y, longitude/x], ...], ...]
 * @param {Boolean} project - whether or not points are lat/lng, and if so project them to X/Y
 * @returns {Array<Array<Array<number>>>} congruent polygon within the bounds of the unit square [[[x, y], ...], ...]
 */
export function fitPolygonsInUnitSquare(shapes, project = true) {
  // project points to web-mercator to prevent squishing closer to the poles
  let projectedShapes = project ? projectShapes(shapes) : extractShapes(shapes);

  // find the bounding box of the points
  let [minX, minY, maxX, maxY] = calculateBounds(projectedShapes);

  // get the size of the bounding box
  let width = maxX - minX;
  let height = maxY - minY;

  // maintain aspect ratio by picking the longest side of the bounding box
  let scaleFactor = Math.max(width, height);

  // center point of the bounding box, used for aligning the shape
  let centerX = width / scaleFactor;
  let centerY = height / scaleFactor;

  return projectedShapes.map(points => points.map(([x, y]) => [
    (x - minX) / scaleFactor + 0.5 - centerX / 2,
    (y - minY) / scaleFactor + 0.5 - centerY / 2
  ]));
}

/**
 * Generates SVG Paths for a lat,lng polygon
 * @param {Array<Array<Array<number>>>} shapes - [[[latitude/y, longitude/x], ...], ...]
 * @param {Number} options.scale - factor to scale the paths by
 * @param {Number} options.padding - applies to inside of svg
 * @param {Boolean} options.project - whether or not points are lat/lng, and if so project them to X/Y
 * @returns {Array<String>} svg path definitions
 */
export function generateSvgPaths(shapes, { scale, padding = 1, project = true } = {}) {
  let paths = fitPolygonsInUnitSquare(shapes, project);

  let paddedScale = scale - padding * 2;

  return paths
    .map(points => points.map(coords => coords.map(c => c * paddedScale + padding)))
    .map(points => {
      let firstPoint = `${points[0][0]} ${points[0][1]}`;
      let restOfPoints = `${points.slice(1).map(([x, y]) => `L${x} ${y}`).join(' ')}`;
      return `M${firstPoint} ${restOfPoints} Z`;
    });
}
